import { z } from "zod";

const UTC_ISO_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/;

export const UtcIsoSchema = z
  .string()
  .refine(
    (value) => UTC_ISO_PATTERN.test(value) && Number.isFinite(Date.parse(value)),
    "Expected a valid UTC ISO-8601 timestamp",
  );

export const CoordinatesSchema = z.object({
  latitude: z.number().finite().min(-90).max(90),
  longitude: z.number().finite().min(-180).max(180),
});

export const StormIdentitySchema = z.object({
  source: z.enum(["qweather", "hko", "demo"]),
  providerId: z.string().trim().min(1).max(128),
  basin: z.string().trim().min(1).max(64),
  year: z.number().int().min(1900).max(2200),
  number: z.number().int().min(0).max(99).nullable(),
  name: z.string().trim().min(1).max(128),
  status: z.enum(["active", "inactive", "ended"]),
});

export type StormIdentity = z.infer<typeof StormIdentitySchema>;

export const WindQuadrantsSchema = z.object({
  northeast: z.number().finite().nonnegative(),
  southeast: z.number().finite().nonnegative(),
  southwest: z.number().finite().nonnegative(),
  northwest: z.number().finite().nonnegative(),
});

export const WindRingSchema = z.object({
  thresholdKt: z.union([z.literal(30), z.literal(50), z.literal(64)]),
  quadrantsKm: WindQuadrantsSchema,
});

export const WindRadiiSchema = z.object({
  validTime: UtcIsoSchema,
  evidence: z.enum(["observed", "interpolated", "extrapolated"]),
  rings: z.array(WindRingSchema).max(3),
});

export type WindRadii = z.infer<typeof WindRadiiSchema>;
export type WindRing = z.infer<typeof WindRingSchema>;

export const StormPointSchema = z.object({
  time: UtcIsoSchema,
  latitude: z.number().finite().min(-90).max(90),
  longitude: z.number().finite().min(-180).max(180),
  maxWindMs: z.number().finite().nonnegative().nullable(),
  pressureHpa: z.number().finite().min(800).max(1_100).nullable(),
  intensity: z.string().trim().min(1).max(64),
  evidence: z.enum(["observed", "forecast", "fallback"]),
});

export type StormPoint = z.infer<typeof StormPointSchema>;

export const CityLocationSchema = z.object({
  id: z.string().trim().min(1).max(128),
  name: z.string().trim().min(1).max(128),
  adm1: z.string().trim().max(128).default(""),
  adm2: z.string().trim().max(128).default(""),
  country: z.string().trim().max(128).default("中国"),
  latitude: z.number().finite().min(-90).max(90),
  longitude: z.number().finite().min(-180).max(180),
});

export type CityLocation = z.infer<typeof CityLocationSchema>;

export const CityHourlyForecastSchema = z.object({
  locationId: z.string().trim().min(1).max(128),
  time: UtcIsoSchema,
  publishedAt: UtcIsoSchema,
  weatherText: z.string().trim().min(1).max(128),
  precipitationMm: z.number().finite().nonnegative(),
  windDirection: z.string().trim().min(1).max(64),
  windSpeedMs: z.number().finite().nonnegative(),
  beaufort: z.number().int().min(0).max(17),
  cloudPercent: z.number().finite().min(0).max(100).nullable(),
});

export type CityHourlyForecast = z.infer<typeof CityHourlyForecastSchema>;

export const ConfidenceSchema = z.enum(["low", "medium", "high"]);
export type Confidence = z.infer<typeof ConfidenceSchema>;

export const RainCategorySchema = z.enum([
  "无明显降雨",
  "小雨",
  "中雨",
  "大雨",
  "暴雨",
]);

export const TyphoonImpactEstimateSchema = z.object({
  time: UtcIsoSchema.nullable().default(null),
  distanceKm: z.number().finite().nonnegative(),
  quadrant: z.enum(["northeast", "southeast", "southwest", "northwest"]),
  windBeaufort: z.tuple([
    z.number().int().min(0).max(17),
    z.number().int().min(0).max(17),
  ]),
  rainfallMm: z.tuple([z.number().finite().nonnegative(), z.number().finite().nonnegative()]),
  rainCategory: RainCategorySchema,
  confidence: ConfidenceSchema,
  methodVersion: z.string().trim().min(1),
});

export type TyphoonImpactEstimate = z.infer<typeof TyphoonImpactEstimateSchema>;

export const ComparisonResultSchema = z.object({
  time: UtcIsoSchema,
  status: z.enum(["gap", "aligned", "insufficient"]),
  trigger: z.enum(["wind", "data-quality", "no-significant-impact"]),
  reason: z.string().trim().min(1),
});

export type ComparisonResult = z.infer<typeof ComparisonResultSchema>;

export const ComparisonInputSchema = z.object({
  time: UtcIsoSchema,
  typhoonBeaufortLower: z.number().int().min(0).max(17).nullable(),
  forecastBeaufortUpper: z.number().int().min(0).max(17).nullable(),
  confidence: ConfidenceSchema,
  stormPublishedAt: UtcIsoSchema,
  weatherPublishedAt: UtcIsoSchema,
  leadHours: z.number().finite().nonnegative(),
});

export type ComparisonInput = z.infer<typeof ComparisonInputSchema>;

export function stormIdentityKey(identity: StormIdentity): string {
  const parsed = StormIdentitySchema.parse(identity);
  return [parsed.source, parsed.basin, parsed.year, parsed.providerId]
    .map((part) => encodeURIComponent(String(part)))
    .join(":");
}

export function normalizeUtc(value: string): string {
  if (!/(?:Z|[+-]\d{2}:\d{2})$/i.test(value)) {
    throw new TypeError("Timestamp must include a UTC marker or numeric offset");
  }

  const epochMs = Date.parse(value);
  if (!Number.isFinite(epochMs)) {
    throw new TypeError("Invalid ISO-8601 timestamp");
  }

  return new Date(epochMs).toISOString();
}

export interface Coordinates {
  latitude: number;
  longitude: number;
}

const GCJ_A = 6_378_245;
const GCJ_EE = 0.006693421622965943;

function outsideMainlandChina({ latitude, longitude }: Coordinates): boolean {
  return longitude < 72.004 || longitude > 137.8347 || latitude < 0.8293 || latitude > 55.8271;
}

function transformLatitude(x: number, y: number): number {
  let result = -100 + 2 * x + 3 * y + 0.2 * y * y + 0.1 * x * y + 0.2 * Math.sqrt(Math.abs(x));
  result += ((20 * Math.sin(6 * x * Math.PI) + 20 * Math.sin(2 * x * Math.PI)) * 2) / 3;
  result += ((20 * Math.sin(y * Math.PI) + 40 * Math.sin((y / 3) * Math.PI)) * 2) / 3;
  result += ((160 * Math.sin((y / 12) * Math.PI) + 320 * Math.sin((y * Math.PI) / 30)) * 2) / 3;
  return result;
}

function transformLongitude(x: number, y: number): number {
  let result = 300 + x + 2 * y + 0.1 * x * x + 0.1 * x * y + 0.1 * Math.sqrt(Math.abs(x));
  result += ((20 * Math.sin(6 * x * Math.PI) + 20 * Math.sin(2 * x * Math.PI)) * 2) / 3;
  result += ((20 * Math.sin(x * Math.PI) + 40 * Math.sin((x / 3) * Math.PI)) * 2) / 3;
  result += ((150 * Math.sin((x / 12) * Math.PI) + 300 * Math.sin((x / 30) * Math.PI)) * 2) / 3;
  return result;
}

function gcjDelta({ latitude, longitude }: Coordinates): Coordinates {
  let latitudeDelta = transformLatitude(longitude - 105, latitude - 35);
  let longitudeDelta = transformLongitude(longitude - 105, latitude - 35);
  const radLatitude = (latitude / 180) * Math.PI;
  const magic = 1 - GCJ_EE * Math.sin(radLatitude) ** 2;
  const sqrtMagic = Math.sqrt(magic);
  latitudeDelta =
    (latitudeDelta * 180) /
    (((GCJ_A * (1 - GCJ_EE)) / (magic * sqrtMagic)) * Math.PI);
  longitudeDelta =
    (longitudeDelta * 180) /
    ((GCJ_A / sqrtMagic) * Math.cos(radLatitude) * Math.PI);
  return { latitude: latitudeDelta, longitude: longitudeDelta };
}

export function gcj02ToWgs84(coordinates: Coordinates): Coordinates {
  const parsed = CoordinatesSchema.parse(coordinates);
  if (outsideMainlandChina(parsed)) return parsed;
  const delta = gcjDelta(parsed);
  return {
    latitude: parsed.latitude - delta.latitude,
    longitude: parsed.longitude - delta.longitude,
  };
}

const toRadians = (degrees: number): number => (degrees * Math.PI) / 180;
const toDegrees = (radians: number): number => (radians * 180) / Math.PI;

function sphericalInterpolate(a: Coordinates, b: Coordinates, fraction: number): Coordinates {
  const lat1 = toRadians(a.latitude);
  const lon1 = toRadians(a.longitude);
  const lat2 = toRadians(b.latitude);
  const lon2 = toRadians(b.longitude);
  const vectorA = [
    Math.cos(lat1) * Math.cos(lon1),
    Math.cos(lat1) * Math.sin(lon1),
    Math.sin(lat1),
  ];
  const vectorB = [
    Math.cos(lat2) * Math.cos(lon2),
    Math.cos(lat2) * Math.sin(lon2),
    Math.sin(lat2),
  ];
  const dot = Math.max(-1, Math.min(1, vectorA.reduce((sum, value, index) => sum + value * vectorB[index]!, 0)));
  const omega = Math.acos(dot);

  if (omega < 1e-12) {
    return {
      latitude: a.latitude + (b.latitude - a.latitude) * fraction,
      longitude: a.longitude + (b.longitude - a.longitude) * fraction,
    };
  }

  const sinOmega = Math.sin(omega);
  const weightA = Math.sin((1 - fraction) * omega) / sinOmega;
  const weightB = Math.sin(fraction * omega) / sinOmega;
  const x = weightA * vectorA[0]! + weightB * vectorB[0]!;
  const y = weightA * vectorA[1]! + weightB * vectorB[1]!;
  const z = weightA * vectorA[2]! + weightB * vectorB[2]!;
  const longitude = toDegrees(Math.atan2(y, x));
  return {
    latitude: toDegrees(Math.atan2(z, Math.hypot(x, y))),
    longitude: longitude === -180 ? 180 : longitude,
  };
}

function interpolateOptionalNumber(
  start: number | null,
  end: number | null,
  fraction: number,
): number | null {
  if (start === null || end === null) return start ?? end;
  return start + (end - start) * fraction;
}

export function interpolateStormPoint(
  start: StormPoint,
  end: StormPoint,
  targetTime: string,
): StormPoint {
  const from = StormPointSchema.parse(start);
  const to = StormPointSchema.parse(end);
  const target = Date.parse(normalizeUtc(targetTime));
  const fromTime = Date.parse(from.time);
  const toTime = Date.parse(to.time);
  if (toTime <= fromTime) throw new RangeError("End point must be later than start point");
  if (target < fromTime || target > toTime) throw new RangeError("Target time is outside the interpolation interval");

  const fraction = (target - fromTime) / (toTime - fromTime);
  const coordinates = sphericalInterpolate(from, to, fraction);
  return StormPointSchema.parse({
    time: new Date(target).toISOString(),
    ...coordinates,
    maxWindMs: interpolateOptionalNumber(from.maxWindMs, to.maxWindMs, fraction),
    pressureHpa: interpolateOptionalNumber(from.pressureHpa, to.pressureHpa, fraction),
    intensity: fraction < 0.5 ? from.intensity : to.intensity,
    evidence: fraction < 0.5 ? from.evidence : to.evidence,
  });
}

const KNOTS_TO_METRES_PER_SECOND = 0.514444;

function scaledQuadrants(quadrants: WindRing["quadrantsKm"], scale: number): WindRing["quadrantsKm"] {
  return {
    northeast: quadrants.northeast * scale,
    southeast: quadrants.southeast * scale,
    southwest: quadrants.southwest * scale,
    northwest: quadrants.northwest * scale,
  };
}

const WIND_QUADRANTS = ["northeast", "southeast", "southwest", "northwest"] as const;

export function interpolateWindRadii(
  start: WindRadii,
  end: WindRadii,
  targetTime: string,
): WindRadii {
  const from = WindRadiiSchema.parse(start);
  const to = WindRadiiSchema.parse(end);
  const target = Date.parse(normalizeUtc(targetTime));
  const fromTime = Date.parse(from.validTime);
  const toTime = Date.parse(to.validTime);
  if (toTime <= fromTime) throw new RangeError("End wind radii must be later than start wind radii");
  if (target < fromTime || target > toTime) {
    throw new RangeError("Target time is outside the wind-radii interpolation interval");
  }

  const fraction = (target - fromTime) / (toTime - fromTime);
  const outerLimit: WindRing["quadrantsKm"] = {
    northeast: Number.POSITIVE_INFINITY,
    southeast: Number.POSITIVE_INFINITY,
    southwest: Number.POSITIVE_INFINITY,
    northwest: Number.POSITIVE_INFINITY,
  };
  const rings = ([30, 50, 64] as const).flatMap((thresholdKt): WindRing[] => {
    const fromRing = from.rings.find((ring) => ring.thresholdKt === thresholdKt);
    const toRing = to.rings.find((ring) => ring.thresholdKt === thresholdKt);
    if (!fromRing || !toRing) return [];
    const quadrantsKm = { ...outerLimit };
    for (const direction of WIND_QUADRANTS) {
      const interpolated =
        fromRing.quadrantsKm[direction] +
        (toRing.quadrantsKm[direction] - fromRing.quadrantsKm[direction]) * fraction;
      quadrantsKm[direction] = Math.max(0, Math.min(outerLimit[direction], interpolated));
      outerLimit[direction] = quadrantsKm[direction];
    }
    return [{ thresholdKt, quadrantsKm }];
  });

  return WindRadiiSchema.parse({
    validTime: new Date(target).toISOString(),
    evidence: "interpolated",
    rings,
  });
}

/**
 * Resolves a wind-radii frame from the complete time series.
 *
 * Only exact matches and two-sided interpolation are handled here. Callers
 * may apply their own bounded persistence or intensity-based extrapolation
 * when the target lies outside the available radius observations.
 */
export function resolveWindRadiiAtTime(
  timeline: WindRadii[],
  targetTime: string,
): WindRadii | null {
  const target = Date.parse(normalizeUtc(targetTime));
  const ordered = timeline
    .map((item) => WindRadiiSchema.parse(item))
    .sort((a, b) => Date.parse(a.validTime) - Date.parse(b.validTime));
  const exact = ordered.find((item) => Date.parse(item.validTime) === target);
  if (exact) return exact;

  const nextIndex = ordered.findIndex((item) => Date.parse(item.validTime) > target);
  if (nextIndex <= 0) return null;
  return interpolateWindRadii(
    ordered[nextIndex - 1]!,
    ordered[nextIndex]!,
    new Date(target).toISOString(),
  );
}

export function extrapolateWindRadii(
  current: WindRadii,
  currentMaxWindMs: number,
  predictedMaxWindMs: number,
  validTime: string,
): WindRadii {
  const parsed = WindRadiiSchema.parse(current);
  if (!Number.isFinite(currentMaxWindMs) || !Number.isFinite(predictedMaxWindMs)) {
    throw new TypeError("Wind speeds must be finite numbers");
  }

  const rings = parsed.rings.flatMap((ring): WindRing[] => {
    const thresholdMs = ring.thresholdKt * KNOTS_TO_METRES_PER_SECOND;
    if (predictedMaxWindMs < thresholdMs || currentMaxWindMs <= thresholdMs) return [];
    const scale = Math.min(
      1.25,
      Math.max(
        0,
        Math.sqrt(
          (predictedMaxWindMs - thresholdMs) /
            (currentMaxWindMs - thresholdMs),
        ),
      ),
    );
    return [{ ...ring, quadrantsKm: scaledQuadrants(ring.quadrantsKm, scale) }];
  });

  return WindRadiiSchema.parse({
    validTime: normalizeUtc(validTime),
    evidence: "extrapolated",
    rings,
  });
}

export function estimateWindRadiiFromCurrentWind(
  currentMaxWindMs: number,
  validTime: string,
): WindRadii {
  if (!Number.isFinite(currentMaxWindMs) || currentMaxWindMs < 0) {
    throw new TypeError("Current maximum wind must be a finite non-negative number");
  }
  const multipliers = {
    northeast: 1.15,
    southeast: 1.05,
    southwest: 0.9,
    northwest: 1,
  } as const;
  const growthPerMs: Record<WindRing["thresholdKt"], number> = {
    30: 11,
    50: 10.5,
    64: 8,
  };
  const rings = ([30, 50, 64] as const).flatMap((thresholdKt): WindRing[] => {
    const thresholdMs = thresholdKt * KNOTS_TO_METRES_PER_SECOND;
    if (currentMaxWindMs < thresholdMs) return [];
    const radius = Math.max(20, (currentMaxWindMs - thresholdMs) * growthPerMs[thresholdKt]);
    return [{
      thresholdKt,
      quadrantsKm: {
        northeast: radius * multipliers.northeast,
        southeast: radius * multipliers.southeast,
        southwest: radius * multipliers.southwest,
        northwest: radius * multipliers.northwest,
      },
    }];
  });
  return WindRadiiSchema.parse({
    validTime: normalizeUtc(validTime),
    evidence: "extrapolated",
    rings,
  });
}

export type StormQuadrant = TyphoonImpactEstimate["quadrant"];

export interface RainfallImpactInput {
  distanceKm: number;
  radius30Km: number;
  maxWindMs: number;
  leadHours: number;
  quadrant: StormQuadrant;
  time?: string;
}

const QUADRANT_RAIN_MULTIPLIER: Record<StormQuadrant, number> = {
  northeast: 1.25,
  southeast: 1.1,
  southwest: 0.8,
  northwest: 0.95,
};

function rainCategory(amountMm: number): TyphoonImpactEstimate["rainCategory"] {
  if (amountMm < 0.1) return "无明显降雨";
  if (amountMm < 10) return "小雨";
  if (amountMm < 25) return "中雨";
  if (amountMm < 50) return "大雨";
  return "暴雨";
}

export function estimateRainfallImpact(input: RainfallImpactInput): TyphoonImpactEstimate {
  const values = [input.distanceKm, input.radius30Km, input.maxWindMs, input.leadHours];
  if (values.some((value) => !Number.isFinite(value) || value < 0) || input.radius30Km === 0) {
    throw new RangeError("Rain impact inputs must be finite, non-negative, and use a positive radius");
  }

  const normalizedDistance = input.distanceKm / input.radius30Km;
  const radialDecay = Math.exp(-0.9 * normalizedDistance ** 1.35);
  const windFactor = Math.min(1.8, Math.max(0.4, input.maxWindMs / 33));
  const leadDecay = Math.exp(-input.leadHours / 60);
  const midpoint =
    55 *
    radialDecay *
    windFactor *
    leadDecay *
    QUADRANT_RAIN_MULTIPLIER[input.quadrant];
  const rainfallMm: [number, number] = [
    Math.max(0, Number((midpoint * 0.6).toFixed(1))),
    Math.max(0, Number((midpoint * 1.4).toFixed(1))),
  ];

  return TyphoonImpactEstimateSchema.parse({
    time: input.time ? normalizeUtc(input.time) : null,
    distanceKm: input.distanceKm,
    quadrant: input.quadrant,
    windBeaufort: [0, 0],
    rainfallMm,
    rainCategory: rainCategory(midpoint),
    confidence: "low",
    methodVersion: "RainImpact-v1",
  });
}

const HOUR_MS = 3_600_000;

function isComparisonDataUsable(row: ComparisonInput): boolean {
  if (row.typhoonBeaufortLower === null || row.forecastBeaufortUpper === null) return false;
  return true;
}

export function classifyImpactComparisons(inputs: ComparisonInput[]): ComparisonResult[] {
  const rows = inputs.map((input) => ComparisonInputSchema.parse(input));
  const gapCandidate = rows.map(
    (row) =>
      isComparisonDataUsable(row) &&
      row.typhoonBeaufortLower! >= 7 &&
      row.typhoonBeaufortLower! - row.forecastBeaufortUpper! >= 2,
  );
  const sustainedGap = gapCandidate.map((candidate, index) => {
    if (!candidate) return false;
    const previousIsAdjacent =
      index > 0 &&
      gapCandidate[index - 1] &&
      Date.parse(rows[index]!.time) - Date.parse(rows[index - 1]!.time) <= 1.5 * HOUR_MS;
    const nextIsAdjacent =
      index < rows.length - 1 &&
      gapCandidate[index + 1] &&
      Date.parse(rows[index + 1]!.time) - Date.parse(rows[index]!.time) <= 1.5 * HOUR_MS;
    return Boolean(previousIsAdjacent || nextIsAdjacent);
  });

  return rows.map((row, index) => {
    if (!isComparisonDataUsable(row)) {
      return ComparisonResultSchema.parse({
        time: row.time,
        status: "insufficient",
        trigger: "data-quality",
        reason: "当前信息不支持可靠比较",
      });
    }
    if (sustainedGap[index]) {
      return ComparisonResultSchema.parse({
        time: row.time,
        status: "gap",
        trigger: "wind",
        reason: "台风影响估计显著高于城市预报",
      });
    }
    if (
      row.typhoonBeaufortLower! >= 7 &&
      Math.abs(row.typhoonBeaufortLower! - row.forecastBeaufortUpper!) <= 1
    ) {
      return ComparisonResultSchema.parse({
        time: row.time,
        status: "aligned",
        trigger: "wind",
        reason: "两者基本相符",
      });
    }
    return ComparisonResultSchema.parse({
      time: row.time,
      status: "insufficient",
      trigger:
        row.typhoonBeaufortLower! >= 7 ? "data-quality" : "no-significant-impact",
      reason:
        row.typhoonBeaufortLower! >= 7
          ? "单个时段尚不能形成可靠结论"
          : "当前时段无显著台风影响",
    });
  });
}
